import { useEffect, useId, useMemo, useState } from 'react';
import { CheckIcon, PencilIcon, PlusIcon, UserPlusIcon } from 'lucide-react';
import {
  naamsleutel,
  opdrachtenTekst,
  SENIORITEITEN,
  type OpdrachtRij,
  type Seniority,
} from '@vip/shared';

import { OpdrachtenDialog } from '@/components/OpdrachtenDialog';

import { AdministratieWaarschuwing } from '@/components/AdministratieWaarschuwing';
import { Alert, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldError, FieldLabel, FieldLegend, FieldSet } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemGroup,
  ItemMedia,
  ItemTitle,
} from '@/components/ui/item';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import { ApiError } from '../../lib/apiClient';
import { useAuth } from '../auth/useAuth';
import {
  useBegeleiding,
  useKoppelAanMij,
  useMaakOpdracht,
  useMaakPersoon,
  useZetNiveau,
  useZetOpdracht,
} from './begeleiding.api';
import { useDeelnemers } from './gesprekken.api';

export type RosterRegel = { participantId: string; seniority: Seniority };

const niveauLabel = (s: Seniority) => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * De deelnemerslijst van "Nieuw gesprek". Begint gevuld met de eigen
 * gecoachten (§ begeleiding:manage:own), uitgevinkt: een per ongeluk
 * aangevinkte rij is niet meer te corrigeren, want de enige PATCH op een
 * gesprek doet alleen de titel.
 *
 * Drie stukken lokale staat, nooit gevuld uit een effect: `aan` (wie erbij
 * was), `niveaus` (alleen afwijkingen van het voorstel) en `extra` (wie de
 * buddy zelf heeft bijgezet, gekozen of net aangemaakt). De rijen zelf zijn
 * elke render afgeleid uit `gecoachten` plus `extra`.
 */
export function Deelnemers({
  bezig,
  onChange,
}: {
  bezig: boolean;
  onChange: (roster: RosterRegel[]) => void;
}) {
  const { user } = useAuth();
  const { data: begeleiding } = useBegeleiding();
  const { data: deelnemers } = useDeelnemers();
  const zetOpdracht = useZetOpdracht();
  const zetNiveau = useZetNiveau();
  const koppelAanMij = useKoppelAanMij();

  const [aan, setAan] = useState<Set<string>>(new Set());
  const [niveaus, setNiveaus] = useState<Map<string, Seniority>>(new Map());
  const [extra, setExtra] = useState<Array<{ id: string; naam: string }>>([]);
  /** Wie er nu wordt bewerkt, en welk venster daarbij openstaat. Twee losse
   *  vensters: het niveau geldt alleen voor dit gesprek, de opdracht verandert
   *  de administratie. Dat zijn twee verschillende beloftes en die horen niet
   *  in hetzelfde venster. */
  const [bewerken, setBewerken] = useState<{ id: string; wat: 'niveau' | 'opdracht' } | null>(null);
  /** Wie er wordt gekoppeld: een vrije collega die de buddy net aanwees als
   *  "ik begeleid deze persoon". */
  const [koppelen, setKoppelen] = useState<string | null>(null);
  const [fout, setFout] = useState('');
  const maakOpdracht = useMaakOpdracht();

  // Elke stap hieronder gememoized op zijn eigen invoer: `.filter()` en `new
  // Set()` maken anders bij elke render een nieuwe referentie, ook als er
  // niets veranderde. `roster` erft die instabiliteit dan door naar de
  // effect eronder, en dat is een oneindige lus: elke render zet een nieuwe
  // roster, elke nieuwe roster triggert de volgende render.
  const gecoachten = useMemo(() => begeleiding?.gecoachten ?? [], [begeleiding]);
  const opdrachtgevers = useMemo(() => begeleiding?.opdrachtgevers ?? [], [begeleiding]);
  const gecoachtenIds = useMemo(() => new Set(gecoachten.map((g) => g.id)), [gecoachten]);
  const extraRijen = useMemo(
    () => extra.filter((e) => !gecoachtenIds.has(e.id)),
    [extra, gecoachtenIds],
  );

  function niveauVoor(id: string, standaard: Seniority | null): Seniority {
    if (niveaus.has(id)) return niveaus.get(id)!;
    if (standaard) return standaard;
    return deelnemers?.find((d) => d.id === id)?.vorigeSeniority ?? 'junior';
  }

  /** Buddy en opdrachten van een extra rij, opgezocht in `deelnemers`. Staat
   *  een net aangemaakte persoon daar nog niet in (de refetch loopt nog), dan
   *  telt hij als een vrije collega zonder opdracht. */
  function collegaInfo(id: string): { buddyId: string | null; opdrachten: OpdrachtRij[] } {
    const d = deelnemers?.find((p) => p.id === id);
    return { buddyId: d?.buddyId ?? null, opdrachten: d?.opdrachten ?? [] };
  }

  function relatieVoor(buddyId: string | null): 'eigen' | 'vrij' | 'andere-buddy' {
    if (buddyId === null) return 'vrij';
    return buddyId === user?.id ? 'eigen' : 'andere-buddy';
  }

  const toggleAan = (id: string) =>
    setAan((s) => {
      const volgende = new Set(s);
      if (volgende.has(id)) volgende.delete(id);
      else volgende.add(id);
      return volgende;
    });

  const roster = useMemo<RosterRegel[]>(() => {
    const alle = [
      ...gecoachten.map((g) => ({ id: g.id, standaard: g.niveau })),
      ...extraRijen.map((e) => ({ id: e.id, standaard: null as Seniority | null })),
    ];
    return alle
      .filter((r) => aan.has(r.id))
      .map((r) => ({ participantId: r.id, seniority: niveauVoor(r.id, r.standaard) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- niveauVoor sluit al aan/niveaus/deelnemers in, hier los opnemen zou dezelfde afhankelijkheden dubbel maken
  }, [gecoachten, extraRijen, aan, niveaus, deelnemers]);

  // Geeft de afgeleide roster door aan de ouder. Geen spiegelend effect op
  // servergegevens: `aan`/`niveaus`/`extra` komen nooit uit een effect, alleen
  // uit een klik van de buddy. `onChange` is `setRoster` uit `useState` in
  // `NieuwGesprek.tsx` en dus stabiel over renders heen.
  useEffect(() => onChange(roster), [roster, onChange]);

  const zichtbareIds = new Set([...gecoachtenIds, ...extraRijen.map((e) => e.id)]);
  const beschikbaar = (deelnemers ?? []).filter((d) => !zichtbareIds.has(d.id));

  function voegBestaandeToe(id: string) {
    const persoon = deelnemers?.find((d) => d.id === id);
    if (!persoon) return;
    setExtra((e) => [...e, { id, naam: persoon.displayName }]);
    setAan((s) => new Set(s).add(id));
  }

  async function wijzigOpdrachten(id: string, opdrachtIds: string[]) {
    setFout('');
    try {
      await zetOpdracht.mutateAsync({ id, opdrachtIds });
    } catch (err) {
      setFout(err instanceof ApiError ? err.message : String(err));
    }
  }

  /** Het niveau van deze roster én het huidige niveau in de administratie.
   *  Mislukt dat tweede, dan blijft de rosterkeuze staan: dit gesprek hoeft
   *  niet te wachten op iets dat de Admin ook kan rechtzetten. */
  async function wijzigNiveau(id: string, niveau: Seniority, ookAdministratie: boolean) {
    setNiveaus((m) => new Map(m).set(id, niveau));
    if (!ookAdministratie) return;
    setFout('');
    try {
      await zetNiveau.mutateAsync({ id, niveau });
    } catch (err) {
      setFout(err instanceof ApiError ? err.message : String(err));
    }
  }

  // Wie er in het open venster staat, opgezocht bij de twee bronnen die er
  // zijn: een gecoachte of een net bijgezette extra rij.
  const bewerkGecoachte = gecoachten.find((g) => g.id === bewerken?.id);
  const bewerkExtra = extraRijen.find((e) => e.id === bewerken?.id);
  const bewerkPersoon = bewerkGecoachte
    ? {
        naam: bewerkGecoachte.naam,
        niveau: niveauVoor(bewerkGecoachte.id, bewerkGecoachte.niveau),
        opdrachten: bewerkGecoachte.opdrachten,
        isEigenGecoachte: true,
      }
    : bewerkExtra
      ? {
          naam: bewerkExtra.naam,
          niveau: niveauVoor(bewerkExtra.id, null),
          opdrachten: collegaInfo(bewerkExtra.id).opdrachten,
          isEigenGecoachte: false,
        }
      : null;

  // Wie er wordt gekoppeld, voor de tekst van de waarschuwing hieronder.
  const teKoppelen = extraRijen.find((e) => e.id === koppelen);

  /** Koppelen, en bij succes meteen het opdrachtvenster klaarzetten: die
   *  opent zodra de verse gecoachten binnen zijn (`bewerkGecoachte` vindt hem
   *  dan). Mislukt het koppelen, dan blijft dit gesprek erbuiten: de fout gaat
   *  naar de gewone melding. */
  async function bevestigKoppelen(id: string) {
    setFout('');
    try {
      await koppelAanMij.mutateAsync(id);
      setBewerken({ id, wat: 'opdracht' });
    } catch (err) {
      setFout(err instanceof ApiError ? err.message : String(err));
    } finally {
      setKoppelen(null);
    }
  }

  return (
    <FieldSet>
      <FieldLegend variant="label">Wie waren erbij</FieldLegend>
      <ItemGroup className="gap-2">
        {/* Jij, zonder dat je iets hoeft te kiezen. Je bent de buddy omdat je
            dit gesprek uploadt, en niet omdat je jezelf uit de lijst met
            collega's hebt aangewezen. */}
        <Item variant="muted">
          <ItemContent>
            <ItemTitle>{user?.naam}</ItemTitle>
          </ItemContent>
          <ItemActions>
            <Badge variant="outline">buddy</Badge>
          </ItemActions>
        </Item>

        {gecoachten.map((g) => (
          <DeelnemerRegel
            aan={aan.has(g.id)}
            bezig={bezig}
            key={g.id}
            naam={g.naam}
            niveau={niveauVoor(g.id, g.niveau)}
            onKoppelen={() => {}}
            onNiveauBewerken={() => setBewerken({ id: g.id, wat: 'niveau' })}
            onOpdrachtBewerken={() => setBewerken({ id: g.id, wat: 'opdracht' })}
            onToggle={() => toggleAan(g.id)}
            opdrachten={g.opdrachten}
            relatie="eigen"
          />
        ))}

        {extraRijen.map((e) => {
          const { buddyId, opdrachten } = collegaInfo(e.id);
          return (
            <DeelnemerRegel
              aan={aan.has(e.id)}
              bezig={bezig}
              key={e.id}
              naam={e.naam}
              niveau={niveauVoor(e.id, null)}
              onKoppelen={() => setKoppelen(e.id)}
              onNiveauBewerken={() => setBewerken({ id: e.id, wat: 'niveau' })}
              onOpdrachtBewerken={() => setBewerken({ id: e.id, wat: 'opdracht' })}
              onToggle={() => toggleAan(e.id)}
              opdrachten={opdrachten}
              relatie={relatieVoor(buddyId)}
            />
          );
        })}
      </ItemGroup>

      <PersoonToevoegen
        beschikbaar={beschikbaar}
        bezig={bezig}
        onKiesBestaande={voegBestaandeToe}
        onToegevoegd={(id, naam, niveau) => {
          setExtra((e) => [...e, { id, naam }]);
          setAan((s) => new Set(s).add(id));
          setNiveaus((m) => new Map(m).set(id, niveau));
        }}
        zichtbareNamen={(deelnemers ?? []).map((d) => d.displayName)}
      />

      {fout && (
        <Alert variant="destructive">
          <AlertTitle>{fout}</AlertTitle>
        </Alert>
      )}

      <NiveauDialog
        isEigenGecoachte={bewerkPersoon?.isEigenGecoachte ?? false}
        naam={bewerkPersoon?.naam ?? ''}
        niveau={bewerkPersoon?.niveau ?? 'junior'}
        onBewaar={(n) => {
          if (bewerken) void wijzigNiveau(bewerken.id, n, bewerkPersoon?.isEigenGecoachte ?? false);
          setBewerken(null);
        }}
        onSluit={() => setBewerken(null)}
        open={bewerken?.wat === 'niveau' && bewerkPersoon !== null}
      />

      <OpdrachtenDialog
        naam={bewerkPersoon?.naam ?? ''}
        onBewaar={(opdrachtIds) => {
          if (bewerken) void wijzigOpdrachten(bewerken.id, opdrachtIds);
          setBewerken(null);
        }}
        onFout={setFout}
        onMaakOpdracht={(invoer) => maakOpdracht.mutateAsync(invoer)}
        onSluit={() => setBewerken(null)}
        opdrachten={bewerkPersoon?.opdrachten ?? []}
        opdrachtgevers={opdrachtgevers}
        open={bewerken?.wat === 'opdracht' && bewerkPersoon !== null}
        waarschuwing={`Je wijzigt de opdrachten van ${bewerkPersoon?.naam ?? ''} in de administratie.`}
      />

      <AdministratieWaarschuwing
        actie={`Je koppelt ${teKoppelen?.naam ?? ''} aan jou als gecoachte.`}
        bezig={koppelAanMij.isPending}
        onAnnuleer={() => setKoppelen(null)}
        onBevestig={() => teKoppelen && void bevestigKoppelen(teKoppelen.id)}
        open={teKoppelen !== undefined}
      />
    </FieldSet>
  );
}

/**
 * Eén rij. De rand zegt of iemand meegaat, niet een los vinkje: `ring-2
 * ring-primary` is dezelfde manier waarop `GesprekKaart` een kaart opvallend
 * maakt. Klik overal op de rij om te wisselen.
 *
 * Niveau en opdracht staan als chips onder de naam en niet als dropdowns:
 * ze worden zelden gewijzigd, en twee altijd-open keuzelijsten maken van een
 * lijstje mensen een formulier. Elke chip opent zijn eigen venster, want de
 * twee beloven iets anders: het niveau geldt alleen voor dit gesprek, de
 * opdracht verandert de administratie.
 *
 * De opdracht is één chip, ook als het er meer zijn: dan de eerste met een
 * teller erachter. Drie namen naast elkaar maken van de regel een alinea.
 */
function DeelnemerRegel({
  naam,
  niveau,
  opdrachten,
  relatie,
  aan,
  bezig,
  onToggle,
  onNiveauBewerken,
  onOpdrachtBewerken,
  onKoppelen,
}: {
  naam: string;
  niveau: Seniority;
  opdrachten: readonly OpdrachtRij[];
  /** Drie situaties naast de opdracht: `eigen` krijgt de opdrachtchip met
   *  potlood (dezelfde grens die de server afdwingt in `zetOpdracht`), `vrij`
   *  (geen buddy) krijgt een chip om hem aan de huidige buddy te koppelen, met
   *  zijn opdracht ernaast als die er al staat, en `andere-buddy` krijgt zijn
   *  opdracht alleen te zien, als platte tekst. */
  relatie: 'eigen' | 'vrij' | 'andere-buddy';
  aan: boolean;
  bezig: boolean;
  onToggle: () => void;
  onNiveauBewerken: () => void;
  onOpdrachtBewerken: () => void;
  onKoppelen: () => void;
}) {
  const uit = bezig || !aan;

  return (
    <Item
      aria-checked={aan}
      aria-label={`${naam} was erbij`}
      className={cn(
        'items-start',
        aan ? 'ring-2 ring-primary/40' : 'opacity-60',
        !bezig && 'cursor-pointer',
      )}
      onClick={() => !bezig && onToggle()}
      onKeyDown={(e) => {
        if (bezig || (e.key !== ' ' && e.key !== 'Enter')) return;
        e.preventDefault();
        onToggle();
      }}
      role="checkbox"
      tabIndex={bezig ? -1 : 0}
      variant="outline"
    >
      <ItemMedia
        className={cn(
          'mt-0.5 size-5 rounded-full border transition-colors',
          aan ? 'border-primary bg-primary text-primary-foreground' : 'border-input',
        )}
      >
        {aan && <CheckIcon className="size-3.5" />}
      </ItemMedia>
      <ItemContent className="min-w-0 gap-2">
        <ItemTitle className="truncate">{naam}</ItemTitle>
        <div className="flex flex-wrap items-center gap-1.5">
          <Chip
            aria-label={`Niveau van ${naam} wijzigen`}
            disabled={uit}
            onClick={onNiveauBewerken}
          >
            {niveauLabel(niveau)}
          </Chip>
          {relatie === 'eigen' && (
            <Chip
              aria-label={`Opdracht van ${naam} wijzigen`}
              disabled={uit}
              leeg={opdrachten.length === 0}
              onClick={onOpdrachtBewerken}
            >
              {opdrachten[0]
                ? `${opdrachten[0].opdrachtgever} · ${opdrachtenTekst(opdrachten)}`
                : 'Geen opdracht'}
            </Chip>
          )}
          {relatie === 'vrij' && (
            <>
              {opdrachten.length > 0 && <OpdrachtLabel opdrachten={opdrachten} />}
              <Chip disabled={uit} icon={UserPlusIcon} onClick={onKoppelen}>
                Ik begeleid deze persoon
              </Chip>
            </>
          )}
          {relatie === 'andere-buddy' && <OpdrachtLabel opdrachten={opdrachten} />}
        </div>
      </ItemContent>
    </Item>
  );
}

/** De opdracht alleen om te lezen, in de vorm van de chip maar zonder knop:
 *  wat er staat is van een andere buddy of van de Admin. */
function OpdrachtLabel({ opdrachten }: { opdrachten: readonly OpdrachtRij[] }) {
  return (
    <Badge className={cn(opdrachten.length === 0 && 'text-muted-foreground')} variant="outline">
      {opdrachten[0]
        ? `${opdrachten[0].opdrachtgever} · ${opdrachtenTekst(opdrachten)}`
        : 'Geen opdracht'}
    </Badge>
  );
}

/** Een klein, klikbaar labeltje, met een potlood tenzij een ander icoon is
 *  meegegeven. Vangt de klik af zodat hij de rij eronder niet aan- of uitzet. */
function Chip({
  children,
  disabled,
  leeg,
  icon: Icon = PencilIcon,
  onClick,
  ...props
}: {
  children: React.ReactNode;
  disabled: boolean;
  /** Nog niets ingevuld: gedempt in plaats van als waarde gepresenteerd. */
  leeg?: boolean;
  icon?: typeof PencilIcon;
  onClick: () => void;
} & React.ComponentProps<'button'>) {
  return (
    <Button
      className={cn('rounded-full', leeg && 'text-muted-foreground')}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      size="xs"
      variant="outline"
      {...props}
    >
      {children}
      <Icon data-icon="inline-end" />
    </Button>
  );
}

/**
 * Het niveau van een deelnemer. Doet twee dingen tegelijk, en dat is waarom de
 * waarschuwing er hoort: het zet de bevroren stand van dít gesprek
 * (`MeetingParticipant.seniority`) én werkt `Persoon.niveau` bij, het huidige
 * niveau in de administratie. Eerdere gesprekken houden hun eigen stand, dus
 * een bevordering herschrijft geen historie; dat is precies waarvoor die twee
 * kolommen naast elkaar bestaan.
 *
 * Bij een deelnemer die geen eigen gecoachte is blijft het bij dit gesprek: de
 * server laat een buddy alleen aan zijn eigen gecoachten komen.
 */
function NiveauDialog({
  naam,
  niveau,
  isEigenGecoachte,
  open,
  onBewaar,
  onSluit,
}: {
  naam: string;
  niveau: Seniority;
  isEigenGecoachte: boolean;
  open: boolean;
  onBewaar: (n: Seniority) => void;
  onSluit: () => void;
}) {
  const [keuze, setKeuze] = useState<Seniority>(niveau);
  const [bevestigen, setBevestigen] = useState(false);

  const gewijzigd = keuze !== niveau;

  return (
    <Dialog onOpenChange={(o) => !o && onSluit()} open={open}>
      <DialogContent
        className="sm:max-w-sm"
        onOpenAutoFocus={() => {
          setKeuze(niveau);
          setBevestigen(false);
        }}
      >
        <DialogHeader>
          <DialogTitle>Niveau van {naam}</DialogTitle>
        </DialogHeader>

        <Field>
          <FieldLabel htmlFor="niveau-keuze">Niveau</FieldLabel>
          <Select onValueChange={(w) => setKeuze(w as Seniority)} value={keuze}>
            <SelectTrigger className="w-full" id="niveau-keuze">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {SENIORITEITEN.map((s) => (
                  <SelectItem key={s} value={s}>
                    {niveauLabel(s)}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </Field>

        <DialogFooter>
          <Button
            disabled={!gewijzigd}
            onClick={() => (isEigenGecoachte ? setBevestigen(true) : onBewaar(keuze))}
          >
            Opslaan
          </Button>
        </DialogFooter>
      </DialogContent>

      <AdministratieWaarschuwing
        onAnnuleer={() => setBevestigen(false)}
        onBevestig={() => {
          setBevestigen(false);
          onBewaar(keuze);
        }}
        open={bevestigen}
        actie={`Je wijzigt het niveau van ${naam} in de administratie.`}
      />
    </Dialog>
  );
}

/**
 * Eén ingang voor elke deelnemer die nog geen rij is: typ een naam, en die
 * bestaat al of niet. Bestaat hij, in de collegalijst, dan is hij met één
 * druk toegevoegd met zijn eigen niveau. Bestaat hij niet, dan verschijnen
 * niveau en de vraag of dit een gecoachte is, ja als standaard, want voor een
 * persoon die nog niet bestaat kan er geen koppeling van een collega worden
 * afgepakt.
 */
function PersoonToevoegen({
  beschikbaar,
  bezig,
  zichtbareNamen,
  onKiesBestaande,
  onToegevoegd,
}: {
  beschikbaar: Array<{ id: string; displayName: string }>;
  bezig: boolean;
  zichtbareNamen: string[];
  onKiesBestaande: (id: string) => void;
  onToegevoegd: (id: string, naam: string, niveau: Seniority) => void;
}) {
  const maakPersoon = useMaakPersoon();
  const koppelAanMij = useKoppelAanMij();
  const eigenId = useId();
  const lijstId = `namen-${eigenId}`;
  const naamId = `naam-${eigenId}`;
  const niveauId = `niveau-${eigenId}`;
  const gecoachteId = `gecoachte-${eigenId}`;

  const [open, setOpen] = useState(false);
  const [naam, setNaam] = useState('');
  const [niveau, setNiveau] = useState<Seniority | ''>('');
  const [isGecoachte, setIsGecoachte] = useState(true);
  const [serverFout, setServerFout] = useState('');
  const [bevestigen, setBevestigen] = useState(false);

  if (!open) {
    return (
      <Button className="self-start" onClick={() => setOpen(true)} size="sm" variant="outline">
        <PlusIcon data-icon="inline-start" />
        Iemand toevoegen
      </Button>
    );
  }

  function sluiten() {
    setNaam('');
    setNiveau('');
    setServerFout('');
    setBevestigen(false);
    setOpen(false);
  }

  const sleutel = naamsleutel(naam.trim());
  const bestaande = naam.trim()
    ? beschikbaar.find((d) => naamsleutel(d.displayName) === sleutel)
    : undefined;
  const lokaleTreffer =
    !bestaande && naam.trim().length > 0 && zichtbareNamen.some((n) => naamsleutel(n) === sleutel);
  const fout = lokaleTreffer ? 'Er staat al iemand met deze naam in de administratie.' : serverFout;
  const magOpslaan = bestaande ? true : Boolean(naam.trim() && niveau && !lokaleTreffer);

  // Pas een lijst tonen zodra er iets getypt is, en dan gefilterd en begrensd:
  // zonder filter toont de browser bij een lege waarde meteen alle namen in
  // één keer, van boven naar onder het scherm. Zelfde patroon als
  // `OnderwerpVeld` in HulpvraagReview.tsx.
  const zoek = naam.trim().toLowerCase();
  const suggesties = zoek
    ? zichtbareNamen
        .filter((n) => n.toLowerCase().includes(zoek) && n.toLowerCase() !== zoek)
        .slice(0, 8)
    : [];

  /** Een bestaande collega kiezen raakt alleen dit gesprek en gaat dus meteen
   *  door. Een nieuwe persoon maakt een rij in de administratie, en dat is de
   *  zwaarste van de drie handelingen hier: eerst bevestigen. */
  function toevoegenGeklikt() {
    if (bestaande) {
      onKiesBestaande(bestaande.id);
      sluiten();
      return;
    }
    if (!magOpslaan || !niveau) return;
    setBevestigen(true);
  }

  async function opslaan() {
    if (!magOpslaan || !niveau) return;
    setBevestigen(false);
    setServerFout('');
    try {
      const { id } = await maakPersoon.mutateAsync({ naam: naam.trim(), niveau });
      if (isGecoachte) {
        try {
          await koppelAanMij.mutateAsync(id);
        } catch {
          // De persoon staat er nu; de koppeling is dan werk voor de
          // Admin. Dit gesprek hoeft er niet op te wachten.
        }
      }
      onToegevoegd(id, naam.trim(), niveau);
      sluiten();
    } catch (err) {
      setServerFout(err instanceof ApiError ? err.message : String(err));
    }
  }

  return (
    <div className="flex flex-col gap-4 rounded-lg border p-3">
      <Field data-invalid={fout ? true : undefined}>
        <FieldLabel htmlFor={naamId}>Naam</FieldLabel>
        <Input
          autoComplete="off"
          autoFocus
          disabled={bezig}
          id={naamId}
          list={suggesties.length > 0 ? lijstId : undefined}
          onChange={(e) => {
            setNaam(e.target.value);
            setServerFout('');
          }}
          placeholder="Bijvoorbeeld: Sanne Bergsma"
          value={naam}
        />
        <datalist id={lijstId}>
          {suggesties.map((n) => (
            <option key={n} value={n} />
          ))}
        </datalist>
        {fout && <FieldError>{fout}</FieldError>}
      </Field>

      {/* Alleen bij een naam die nog niet bestaat: een bestaande collega
          brengt zijn eigen niveau mee en is al gekoppeld of niet. */}
      {!bestaande && (
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
          <Field className="sm:w-32">
            <FieldLabel htmlFor={niveauId}>Niveau</FieldLabel>
            <Select
              disabled={bezig}
              onValueChange={(w) => setNiveau(w as Seniority)}
              value={niveau}
            >
              <SelectTrigger className="w-full" id={niveauId}>
                <SelectValue placeholder="Kies" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {SENIORITEITEN.map((s) => (
                    <SelectItem key={s} value={s}>
                      {niveauLabel(s)}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </Field>

          <FieldLabel className="font-normal" htmlFor={gecoachteId}>
            <Field orientation="horizontal">
              <Checkbox
                checked={isGecoachte}
                disabled={bezig}
                id={gecoachteId}
                onCheckedChange={(w) => setIsGecoachte(w === true)}
              />
              <span>Ik begeleid deze persoon</span>
            </Field>
          </FieldLabel>
        </div>
      )}

      <div className="flex justify-end gap-2 border-t pt-3">
        <Button disabled={maakPersoon.isPending} onClick={sluiten} size="sm" variant="ghost">
          Annuleren
        </Button>
        <Button
          disabled={!magOpslaan || bezig || maakPersoon.isPending}
          onClick={toevoegenGeklikt}
          size="sm"
        >
          {maakPersoon.isPending && <Spinner data-icon="inline-start" />}
          {bestaande ? 'Toevoegen aan dit gesprek' : 'Toevoegen'}
        </Button>
      </div>

      <AdministratieWaarschuwing
        actie={
          isGecoachte
            ? `Je voegt ${naam.trim()} toe aan de administratie en koppelt hem of haar aan jou als gecoachte.`
            : `Je voegt ${naam.trim()} toe aan de administratie.`
        }
        bezig={maakPersoon.isPending}
        onAnnuleer={() => setBevestigen(false)}
        onBevestig={() => void opslaan()}
        open={bevestigen}
      />
    </div>
  );
}
